/**
 * Subscription Utils
 * Helper functions for subscription plan management
 */

import { QuotaTypes } from '@flashbacktech/flashbackclient/api';

// Plan tiers
export enum PlanTier {
  FREE = 'Free',
  STARTER = 'Starter',
  PRO = 'Pro',
  BUSINESS = 'Business',
}

// Billing periods
export enum BillingPeriod {
  MONTHLY = 'Monthly',
  YEARLY = 'Yearly',
}

/**
 * Get plan tier from subscription name
 * Handles various naming conventions from the API
 */
export const getPlanTier = (subscriptionName: string | null | undefined): PlanTier => {
  if (!subscriptionName) {
    return PlanTier.FREE;
  }

  const name = subscriptionName.toLowerCase().trim();

  // Check for specific plan names (order matters - check more specific first)
  // Supports both display names (Starter, Pro, …) and prefixed prod names (credits_starter, credits_growth, …)
  if (name.includes('business') || name.includes('enterprise') || name.includes('scale')) {
    return PlanTier.BUSINESS;
  }
  if (name.includes('pro') || name.includes('growth')) {
    return PlanTier.PRO;
  }
  if (name.includes('starter') || name.includes('developer')) {
    return PlanTier.STARTER;
  }
  if (name.includes('free') || name.includes('trial')) {
    return PlanTier.FREE;
  }

  // If nothing matches, return the subscription name as-is if it matches a tier
  const directMatch = Object.values(PlanTier).find((tier) => tier.toLowerCase() === name);
  if (directMatch) {
    return directMatch as PlanTier;
  }

  return PlanTier.FREE;
};

/**
 * Get billing period from PeriodType or subscription data
 */
export const getBillingPeriod = (
  periodType: QuotaTypes.PeriodType | string | null | undefined
): BillingPeriod | null => {
  if (!periodType) return null;

  const period = typeof periodType === 'string' ? periodType.toUpperCase() : periodType;

  if (period === QuotaTypes.PeriodType.MONTHLY || period === 'MONTHLY')
    return BillingPeriod.MONTHLY;
  if (period === QuotaTypes.PeriodType.YEARLY || period === 'YEARLY') return BillingPeriod.YEARLY;

  return null;
};

/**
 * Get full plan display name with period
 * e.g., "Starter (Monthly)", "Pro (Yearly)", "Free Trial"
 */
export const getFullPlanName = (
  subscriptionName: string | null | undefined,
  periodType?: QuotaTypes.PeriodType | string | null
): string => {
  const tier = getPlanTier(subscriptionName);

  if (tier === PlanTier.FREE) {
    return 'Free Trial';
  }

  const period = getBillingPeriod(periodType);
  if (period) {
    return `${tier} (${period})`;
  }

  return tier;
};

/**
 * Get plan color for UI display
 */
export const getPlanColor = (subscriptionName: string | null | undefined): string => {
  const tier = getPlanTier(subscriptionName);

  switch (tier) {
    case PlanTier.BUSINESS:
      return '#7C3AED'; // Purple
    case PlanTier.PRO:
      return '#2563EB'; // Blue
    case PlanTier.STARTER:
      return '#059669'; // Green
    case PlanTier.FREE:
    default:
      return '#6B7280'; // Gray
  }
};

/**
 * Check if a subscription is active and paid (not free)
 */
export const isPaidPlan = (subscriptionName: string | null | undefined): boolean => {
  const tier = getPlanTier(subscriptionName);
  return tier !== PlanTier.FREE;
};

/**
 * Format subscription status for display
 */
export const formatSubscriptionStatus = (status: string | null | undefined): string => {
  if (!status) return 'Unknown';

  const statusUpper = status.toUpperCase();

  switch (statusUpper) {
    case 'ACTIVE':
      return 'Active';
    case 'INACTIVE':
      return 'Inactive';
    case 'CANCELLED':
      return 'Cancelled';
    case 'EXPIRED':
      return 'Expired';
    case 'PENDING':
      return 'Pending';
    default:
      return status;
  }
};

/**
 * Plan limits interface
 */
export interface PlanLimits {
  trafficPublicNodes: string; // e.g., "1TB", "5TB", "35TB"
  trafficPrivateNodes: string; // e.g., "125GB", "400GB", "1.2TB"
  providers: number | 'Unlimited';
  buckets: number | 'Unlimited';
  repositories: number | 'Unlimited';
  privateBridgeNodes: number;
  teamMembers: number;
  hasPayAsYouNeed: boolean;
}

/**
 * Get plan limits based on subscription name
 */
export const getPlanLimits = (subscriptionName: string | null | undefined): PlanLimits => {
  const tier = getPlanTier(subscriptionName);

  switch (tier) {
    case PlanTier.FREE:
      return {
        trafficPublicNodes: '15GB',
        trafficPrivateNodes: '0GB',
        providers: 3,
        buckets: 5,
        repositories: 2,
        privateBridgeNodes: 0,
        teamMembers: 1,
        hasPayAsYouNeed: false,
      };

    case PlanTier.STARTER:
      return {
        trafficPublicNodes: '1TB',
        trafficPrivateNodes: '125GB',
        providers: 3,
        buckets: 25,
        repositories: 5,
        privateBridgeNodes: 3,
        teamMembers: 1,
        hasPayAsYouNeed: true,
      };

    case PlanTier.PRO:
      return {
        trafficPublicNodes: '5TB',
        trafficPrivateNodes: '400GB',
        providers: 5,
        buckets: 50,
        repositories: 10,
        privateBridgeNodes: 10,
        teamMembers: 3,
        hasPayAsYouNeed: true,
      };

    case PlanTier.BUSINESS:
      return {
        trafficPublicNodes: '35TB',
        trafficPrivateNodes: '1.2TB',
        providers: 'Unlimited',
        buckets: 'Unlimited',
        repositories: 'Unlimited',
        privateBridgeNodes: 50,
        teamMembers: 5,
        hasPayAsYouNeed: true,
      };

    default:
      return {
        trafficPublicNodes: '15GB',
        trafficPrivateNodes: '0GB',
        providers: 3,
        buckets: 5,
        repositories: 2,
        privateBridgeNodes: 0,
        teamMembers: 1,
        hasPayAsYouNeed: false,
      };
  }
};
